/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import NotificationToggle from "./NotificationToggle";
import { ReactComponent as XIcon } from "@shared/assets/Icons/CrossIcon.svg";
import { ReactComponent as CheckIcon } from "@shared/assets/Icons/CheckIcon.svg";
import { ReactComponent as EditIcon } from "@shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as UsersIcon } from "@shared/assets/profile-circle.svg";
import { RuleNotificationRowProps } from "../NotificationTypes";
import {
  buildConditionSentence,
  formatInterval,
} from "../Helpers/formatters";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine, SkeletonText } from "@ui-components/Skeleton/GenericSkeleton";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";

const RuleNotificationRow: React.FC<RuleNotificationRowProps> = ({
  notification,
  requireSelection = false,
  defaultRecipients,
  enabled,
  isReadOnly = false,
  onEnable,
  onDisable,
  onSaveParams,
  onManageRecipients,
  isLoading,
  showManageRecipients
}) => {
  const { template: tpl, subscription: sub } = notification;

  //Common bits
  const displayName = tpl.name.replace(/-template$/i, "");
  const rawInterval = sub?.interval ?? tpl.defaultInterval;
  const meta = tpl.thresholdMetadata;

  const hasThresholdExamples =
  meta?.customizable &&
  Array.isArray(meta.examples) &&
  meta.examples.length > 0;


  // Pull out every condition
  const allConds = sub?.conditions ?? [];

  //If any condition.value is a string → pick that first
  const stringCond = allConds.find(c => typeof c.value === 'string');

  //Otherwise pick the first “real” numeric condition (ignore -1 sentinels)
  const numericCond = allConds.find(
    c => typeof c.value === 'number' && c.value >= 0
  );

  // Or fall back to metadata example
  let exampleCond: { operator: string; value: number } | undefined;
  if (
    meta?.customizable &&
    Array.isArray(meta.examples) &&
    typeof meta.examples[0]?.value === 'number'
  ) {
    exampleCond = {
      operator: 'greater_than',
      value: meta.examples[0].value,
    };
  }

  //This is the one to display / edit
  const activeCond = stringCond ?? numericCond;
  const condition = activeCond
    ? { operator: activeCond.operator, value: activeCond.value }
    : exampleCond;

  const [values, setValues] = useState({
    threshold:
      condition && typeof condition.value === 'number'
        ? String(condition.value)
        : '',
    interval: rawInterval,
  });
  const [operator, setOperator] = useState(condition?.operator ?? '');

  useEffect(() => {
    // re‐run the same logic on every subscription change
    const liveAll = sub?.conditions ?? [];
    const liveStr = liveAll.find(c => typeof c.value === 'string');
    const liveNum = liveAll.find(c => typeof c.value === 'number' && c.value >= 0);
    const live = liveStr ?? liveNum;

    setValues({
      threshold:
        live && typeof live.value === 'number' ? String(live.value) : '',
      interval: sub?.interval ?? tpl.defaultInterval,
    });
    setOperator(live?.operator ?? '');
  }, [sub, tpl.defaultInterval]);


    let sentence: string;
    const isStringCondition =
      activeCond && typeof activeCond.value === "string";
    
    if (isStringCondition) {
      // for any “string” alert, ignore the condition entirely
      sentence = `When ${displayName} for ${formatInterval(rawInterval)}`;
    } else if (condition) {
      sentence = buildConditionSentence(
        tpl.description ?? "",
        displayName,
        condition.operator,
        condition.value,
        rawInterval,
        meta
      );
    } else {
      // no subscription yet
      sentence = `Alert every ${formatInterval(rawInterval)}`;
    }

  // Local editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!sub) return;
    setIsSaving(true);
    setLoading(true);
    try {
      await onSaveParams(
        sub.alertId,
        values.threshold,
        values.interval,
        operator
      );
      setIsEditing(false);
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
  };

  if (requireSelection) {
    return (
      <div className="p-3 border rounded-lg flex justify-between items-center">
        <span className="text-sm">{tpl.name}</span>
        <GenericTag
          label="Waiting for selection"
          variant="default"
          size="small"
        />
      </div>
    );
  }

  //Loading/Saving skeleton
  const RuleNotificationSkeleton = () => (
    <div className="space-y-6 my-5">
      {/* Top pills row */}
      <div className="flex items-center gap-4 flex-wrap">
        <SkeletonLine w="120px" h={20} />
        <SkeletonLine w="100px" h={20} />
        <SkeletonLine w="140px" h={20} />
      </div>

      {/* Description */}
      <div className="space-y-5">
        <SkeletonLine w="30%" h={18} />
        <div className="border border-gray-300 rounded p-2">
          <SkeletonText lines={4} widths="random" />
        </div>
      </div>
    </div>
  );
  if (isLoading || isSaving) {
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-1/3" />
        <div className="space-x-2 flex">
          <div className="h-4 bg-gray-300 rounded w-12" />
          <div className="h-4 bg-gray-300 rounded w-12" />
        </div>
      </div>
    );
  }

  //Determine display label & enabled state
  let label = tpl.name;
  if (sub) {
    const suffix = `-${sub.serviceType}-${sub.serviceId}-`;
    const idx = tpl.name.indexOf(suffix);
    if (idx >= 0) label = tpl.name.slice(0, idx);
  }
  const isEnabled = sub?.enabled ?? enabled;
  const interval = sub?.interval ?? tpl?.interval;

  return (
    <div className="p-3 border rounded-lg space-y-2">
      <div className="flex justify-between items-center">
        <LoadingWrapper
          isLoading={loading}
          skeleton={<RuleNotificationSkeleton />}
        />
        <span className="text-sm">
          {displayName}
        </span>
        <div className="flex items-center space-x-2">
          {!isReadOnly && isEnabled && (
            
            <>
              {/* 1) Recipients button */}
                {showManageRecipients && (
                  
                  <button
                    onClick={onManageRecipients}
                    className="p-1 hover:bg-gray-100 rounded"
                    aria-label="Edit recipients"
                  >
                    <UsersIcon className="w-5 h-5 text-gray-600" />
                  </button>
                )}

              {/* 2) Parameters button */}
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 hover:bg-gray-100 rounded"
                  aria-label="Edit parameters"
                >
                  <EditIcon className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </>
          )}

          <NotificationToggle
            id={tpl.id}
            subscriptionId={sub?.id ?? ""}
            subscriberId={sub?.subscriberId}
            label={tpl.name}
            enabled={enabled}
            defaultRecipients={defaultRecipients}
            onEnable={onEnable}
            onDisable={onDisable}
            isReadOnly={isReadOnly}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Conditions/Interval or edit inputs */}
      <div className="text-sm text-gray-700">
        {condition && !isEditing ? (
          <span>{sentence}</span>
        ) : isEditing ? (
          <div className="flex items-center space-x-2">
            {condition && hasThresholdExamples && (
              <GenericInput
                value={values.threshold}
                onChange={(v) =>
                  setValues((prev) => ({ ...prev, threshold: v }))
                }
                placeholder="e.g. 90"
                className="text-sm w-24 md:w-32"
              />
            )}
            <GenericInput
              value={values.interval}
              onChange={(v) => setValues((p) => ({ ...p, interval: v }))}
              placeholder={meta?.type === "string" ? "e.g. 5m, 30s" : "e.g. 3m"}
              className="text-sm w-24 md:w-32"
            />
            <GenericButton
              size="sm"
              onClick={handleSave}
              title="Save"
              label={<CheckIcon className="w-4 h-4 text-white" />}
            />

            <GenericButton
              size="sm"
              onClick={handleCancel}
              title="Cancel"
              label={<XIcon className="w-4 h-4 text-white" />}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RuleNotificationRow;
